import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../../common/Button";
import CustomInput from "../../../../common/CustomInput";
import {
  createMedicalHistory,
  updateMedicalHistory,
} from "../../../../../redux/modules/clinical-services/actions";
import { useDispatch } from "react-redux";
import CommonSlider from "../../../../common/CommonSlider";
import Alert from "../../../../common/Alert";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import { MedicalHistoryTypes, MedicineOptions } from "./data";
import CommonSelect from "../../services/select/common-creatable-select";
import MedicalConditionSelect from "../../services/select/medical-condition";
import MedicationSelect from "../../services/select/medication-select";
import {
  HistoryTypes,
  sourceOptions,
  SymptomControlOptions,
} from "../../../create/options";
import {
  normalizeSourceValue,
  denormalizeSourceValue,
  handleDateOptionChange as handleDateOptionChangeUtil,
} from "../../../../../helpers/functions";

export default function MedicalHistoryCreate({
  open,
  onClose,
  onSkip,
  onSave,
  selectedHistoryType,
  selectedHistory,
  patientId,
  create = true,
  patientMedicalConditions = [],
  patientMedicalStudies = [],
  patientMedicalSurgeries = [],
  isFromPatientBar = false
}) {
  //State Variables
  const dispatch = useDispatch();
  //Local Variables
  const [status, setStatus] = useState(create ? null : selectedHistory.status);
  const [confirmedDate, setConfirmedDate] = useState(
    create ? null : selectedHistory.confirmedDate
  );
  const [genCode, setGenCode] = useState(
    create ? null : selectedHistory.genCode
  );
  const [historyType, setHistoryType] = useState(
    selectedHistoryType && selectedHistoryType.length > 0 ? HistoryTypes.find(v => v.value === selectedHistoryType) : null
  );
  const [condition, setCondition] = useState(null); //vigilance
  const [isDuplicateCondition, setIsDuplicateCondition] = useState(false); //vigilance
  const [selectedMedicine, setSelectedMedicine] = useState(null); //vigilance
  const [symptomControl, setSymptomControl] = useState(null);
  const [source, setSource] = useState(
    create
      ? {
          label: sourceOptions.find((x) => x.value === "patient")?.label,
          value: "patient",
        }
      : selectedHistory.source
  );
  const [diagnosisDate, setDiagnosisDate] = useState(
    create ? null : selectedHistory.date
  );
  const [dateOption, setDateOption] = useState(
    create ? null : (selectedHistory.diagnosisDateOption || selectedHistory.surgeryDateOption || selectedHistory.medicalStudyDateOption)
  );
  const [comments, setComments] = useState(
    create ? "" : selectedHistory.comments
  );
  const [conditionNameInputMode, setConditionNameInputMode] = useState(
    create ? false : selectedHistory.genCode === null ? true : false
  );
  const [medicationNameInputMode, setMedicationNameInputMode] = useState(
    create ? false : selectedHistory.genCode === null ? true : false
  );
  const [openUncodedDrug, setOpenUncodedDrug] = useState(false);
  const [addAnotherHistory, setAddAnotherHistory] = useState(false);
  const getHistoryTitle = () => {
    const typeValue = historyType?.value || selectedHistoryType;
    if (typeValue === MedicalHistoryTypes.Study) {
      return "Medical Study";
    }
    if (typeValue === MedicalHistoryTypes.Surgery) {
      return "Medical Surgeries";
    }
    return "Medical Condition";
  };

  useEffect(() => {
    if (open) {
      setSource(
        create
          ? {
              label: sourceOptions.find((x) => x.value === "patient")?.label,
              value: "patient",
            }
          : selectedHistory.source
      );
      const selectedTypeValue = selectedHistoryType?.value || selectedHistoryType;
      const nextHistoryType = selectedTypeValue ? HistoryTypes.find((v) => v.value === selectedTypeValue) : historyType;
      setHistoryType(nextHistoryType || null);
      setIsDuplicateCondition(false); // Reset duplicate condition state when opening fresh slider
      if (create) {
        setCondition(null);
        setSelectedMedicine(null);
        setSymptomControl(null);
        setDiagnosisDate(null);
        setDateOption(null);
        setComments("");
        setConditionNameInputMode(false);
        setMedicationNameInputMode(false);
        setGenCode(null);
        setStatus(null);
        setConfirmedDate(null);
      }
    }
  }, [open, selectedHistoryType, create]);

  useEffect(() => {
    if (selectedHistory !== null) {
      setSelectedMedicine({
        label: selectedHistory?.medicationName,
        value: selectedHistory?.medicationGenCode,
      });
      const localCondition = {
        label: selectedHistory?.name,
        name: selectedHistory?.name,
        value:
          selectedHistoryType === MedicalHistoryTypes.Study
            ? selectedHistory?.studyGenCode
            : selectedHistoryType === MedicalHistoryTypes.Surgery
            ? selectedHistory?.surgeryGenCode
            : selectedHistory?.medicalConditionGenCode,
        id:
          selectedHistoryType === MedicalHistoryTypes.Study
            ? selectedHistory?.studyGenCode
            : selectedHistoryType === MedicalHistoryTypes.Surgery
            ? selectedHistory?.surgeryGenCode
            : selectedHistory?.medicalConditionGenCode,
      };

      if (!localCondition.value) {
        localCondition.value = localCondition.name;
      }
      setCondition(localCondition);
      setHistoryType(
        create
          ? null
          : {
              label: HistoryTypes.find(
                (x) =>
                  x.value ===
                  (selectedHistoryType?.label || selectedHistoryType)
              )?.label,
              value: selectedHistoryType?.value || selectedHistoryType,
            }
      );
      setSymptomControl(
        create
          ? null
          : (() => {
              const raw = selectedHistory.symptomControlled ?? null;
              // Map null/empty to "not specified" for dropdown display
              const valueToUse = raw == null || raw === "" ? "not specified" : raw;
              const option = SymptomControlOptions.find((x) => x.value === valueToUse);
              return option ? { label: option.label, value: option.value } : null;
            })()
      );
      setSource(
        create
          ? {
              label: sourceOptions.find((x) => x.value === "patient")?.label,
              value: "patient",
            }
          : (() => {
              // Convert backend normalized value back to frontend value
              const frontendSourceValue = denormalizeSourceValue(selectedHistory?.source);
              const sourceOption = sourceOptions.find(
                (x) => x.value === frontendSourceValue
              );
              return {
                label: sourceOption?.label || "Patient",
                value: frontendSourceValue,
              };
            })()
      );
      setDiagnosisDate(create ? null : selectedHistory.date);
      setDateOption(create ? null : (selectedHistory.diagnosisDateOption || selectedHistory.surgeryDateOption || selectedHistory.medicalStudyDateOption));
      setComments(create ? "" : selectedHistory.comments);
      setStatus(create ? "" : selectedHistory.status);
      setConfirmedDate(create ? "" : selectedHistory.confirmedDate);
    }
  }, [selectedHistory]);

  const getGenCodeValue = (type) => {
    if (type === MedicalHistoryTypes.Condition)
      return selectedHistory.medicalConditionGenCode;
    else if (type === MedicalHistoryTypes.Study)
      return selectedHistory.studyGenCode;
    else if (type === MedicalHistoryTypes.Surgery)
      return selectedHistory.surgeryGenCode;
  };


  // Handle date option change
  const handleDateOptionChange = (dateVal, dateOptionVal) => {
    handleDateOptionChangeUtil(dateVal, dateOptionVal, setDiagnosisDate, setDateOption);
  };

  const resetForm = () => {
    setSymptomControl(null);
    setDiagnosisDate(null);
    setDateOption(null);
    setCondition(null);
    setSelectedMedicine(null);
    setGenCode(null);
    setIsDuplicateCondition(false);
    setHistoryType(null);
    setSource("patient");
    setStatus(null);
    setComments("");
    setConfirmedDate(null);
  };

  const getMedicalConditionObject = (create) => {
    let obj = {
      name: condition.label,
      medicalConditionGenCode: conditionNameInputMode ? null : condition.value,
      // Persist string value directly to backend (null maps to "not specified" for explicit option)
      symptomControlled: symptomControl?.value == null || symptomControl?.value === "" ? "not specified" : symptomControl.value,
      source: normalizeSourceValue(source.value),
      date: diagnosisDate,
      diagnosisDateOption: dateOption,
      status: "active",
      comments: comments,
      commentsToClinicalAlert: false,
      overrideDate: new Date(),
      confirmedDate: null,
    };
    if (!create) obj = { ...obj, id: selectedHistory.id };
    return obj;
  };

  const getMedicalStudyObject = (create) => {
    let obj = {
      type: historyType.value,
      name: condition !== null ? condition.label : "",
      medicationName: selectedMedicine.displayText,
      medicationGenCode: selectedMedicine.identifier,
      medicationGenCodeType: selectedMedicine.type,
      studyGenCode: null,
      source: normalizeSourceValue(source.value),
      date: diagnosisDate,
      medicalStudyDateOption: dateOption,
      status: "active",
      comments: comments,
      commentsToClinicalAlert: false,
      overrideDate: new Date(),
      confirmedDate: null,
    };
    if (!create) obj = { ...obj, id: selectedHistory.id };

    return obj;
  };

  const getMedicalSurgeryObject = (create) => {
    let obj = {
      type: historyType.value,
      name: condition.label,
      surgeryGenCode: conditionNameInputMode ? null : condition.value,
      source: normalizeSourceValue(source.value),
      date: diagnosisDate,
      surgeryDateOption: dateOption,
      status: "active",
      comments: comments,
      commentsToClinicalAlert: false,
      overrideDate: new Date(),
      confirmedDate: null,
    };
    if (!create) obj = { ...obj, id: selectedHistory.id };

    return obj;
  };

  const manageStep = (addAnother) => {
    let conditions = [];
    let studies = [];
    let surgeries = [];

    let allFieldsValid = source !== null;

    if (historyType.value === MedicalHistoryTypes.Condition)
      conditions = [getMedicalConditionObject(create)];
    else if (historyType.value === MedicalHistoryTypes.Study)
      studies = [getMedicalStudyObject(create)];
    else if (historyType.value === MedicalHistoryTypes.Surgery)
      surgeries = [getMedicalSurgeryObject(create)];
    let obj = {
      noKnownMedicalHistory: false,
      medicalConditions: conditions,
      medicalStudies: studies,
      surgeries: surgeries,
    };
    if (allFieldsValid) {
      if (create) {
        dispatch(createMedicalHistory(patientId, obj, isFromPatientBar));
      } else {
        dispatch(updateMedicalHistory(patientId, obj, isFromPatientBar));
      }
      resetForm();
      onSave();
      if(!addAnother) {
        onClose(false);
      }
    }
  };

  return (
    <CommonSlider open={open} onClose={onClose}>
      <div className="flex flex-col flex-1 h-full overflow-y-auto">
        <div className="relative flex-1">
          {/* Header */}
          <div className="px-8 py-6 bg-gray-20">
            <div className="flex items-start justify-between space-x-3">
              <div className="flex space-x-1">
                <h2 className="font-bold text-gray-900 text-display-xs">
                  {create ? "Add" : "Edit"} {getHistoryTitle()}
                </h2>
              </div>
              <div className="flex items-center h-7">
                <button
                  type="button"
                  className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                  onClick={() => {
                    setIsDuplicateCondition(false);
                    onClose(false);
                  }}
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
          {isDuplicateCondition && (
            <Alert
              type="warning"
              title={
                "DUPLICATE " + (historyType == null ? "MEDICAL CONDITION" : historyType.label.toUpperCase()) + " - " +
                condition?.label.toUpperCase()
              }
              content={() => ""}
              isWrapped={true}
              hideCross={true}
            />
          )}
          <div className="px-8 py-6">
            <div className="grid grid-cols-12 gap-6">
              <CommonSelect
                onChange={(value) => setHistoryType(value)}
                isCreatable={false}
                options={HistoryTypes}
                label="Medical History Type"
                isRequired={true}
                value={historyType}
                className="col-span-12"
                error={false}
              />
              {historyType?.value !== MedicalHistoryTypes.Study ? (
                <>
                  <MedicalConditionSelect
                    isRequired={true}
                    onNewCreate={(value) => {
                      if (create) setConditionNameInputMode(value);
                    }}
                    value={condition}
                    onChange={(condition) => {
                      setIsDuplicateCondition(false);
                      setCondition(condition);

                      const existingConditionCode =
                        patientMedicalConditions
                          ?.filter(
                            (val) =>
                              val.medicalConditionGenCode !== null &&
                              val.medicalConditionGenCode.length > 0
                          )
                          ?.map((val) => val.medicalConditionGenCode)
                          ?.filter(
                            (value, index, array) =>
                              array.indexOf(value) === index
                          ) ?? [];
                      const existingStudyCode =
                        patientMedicalStudies
                          ?.filter(
                            (val) =>
                              val.studyGenCode !== null &&
                              val.studyGenCode.length > 0
                          )
                          ?.map((val) => val.studyGenCode)
                          ?.filter(
                            (value, index, array) =>
                              array.indexOf(value) === index
                          ) ?? [];
                      const existingSurgerCode =
                        patientMedicalSurgeries
                          ?.filter(
                            (val) =>
                              val.surgeryGenCode !== null &&
                              val.surgeryGenCode.length > 0
                          )
                          ?.map((val) => val.surgeryGenCode)
                          ?.filter(
                            (value, index, array) =>
                              array.indexOf(value) === index
                          ) ?? [];

                      if (condition?.value) {
                        if (existingConditionCode.includes(condition.value)) {
                          setIsDuplicateCondition(true);
                        }
                        if (existingStudyCode.includes(condition.value)) {
                          setIsDuplicateCondition(true);
                        }
                        if (existingSurgerCode.includes(condition.value)) {
                          setIsDuplicateCondition(true);
                        }
                      }
                    }}
                    error={condition && !condition.value}
                    disabled={!create}
                    isClearable={create}
                    showDropdownIndicator={create}
                    label={
                      historyType?.value === MedicalHistoryTypes.Surgery &&
                      "Surgery/ Procedure"
                    }
                    patientMedicalConditions={patientMedicalConditions}
                    patientMedicalStudies={patientMedicalStudies}
                    patientMedicalSurgeries={patientMedicalSurgeries}
                  />
                  {conditionNameInputMode === true && (
                    <>
                      <p className="col-span-12 text-body-sm text-red-200 font-medium">
                        This is an UNCODED MEDICAL CONDITION- No Clinical Check
                        will be performed.
                      </p>
                    </>
                  )}
                </>
              ) : (
                <CustomInput
                  name="medical study"
                  className={`col-span-12 ${condition?.name ? "bg-white" : "bg-blue-100"}`}
                  inputClassName="bg-transparent"
                  label="Medical Study"
                  placeholder="Enter Medical Study"
                  value={condition?.name || ""}
                  onChange={(e) => {
                    setCondition({
                      label: e.target.value,
                      name: e.target.value,
                      value: null,
                      id: null,
                    });
                  }}
                  type="text"
                  required={true}
                  disabled={!create}
                  showInputCancellation={create}
                  onClose={() => {
                    if (create) setCondition(null);
                  }}
                />
              )}
              {historyType?.value === MedicalHistoryTypes.Condition && (
                <CommonSelect
                  onChange={(value) => setSymptomControl(value)}
                  isCreatable={false}
                  options={SymptomControlOptions}
                  label="Symptom Control"
                  isRequired={false}
                  value={symptomControl}
                  className="col-span-12"
                  error={false}
                />
              )}
              {historyType?.value === MedicalHistoryTypes.Study && (
                <>
                  {
                    <MedicationSelect
                      onChange={(medicine) => {
                        setSelectedMedicine(medicine);
                      }}
                      isRequired={false}
                      onNewCreate={(value) => setMedicationNameInputMode(value)}
                      error={false}
                      value={selectedMedicine}
                      defaultOptions={MedicineOptions}
                      showDin={true}
                    />
                  }
                  {medicationNameInputMode === true && (
                    <>
                      <p className="text-sm text-pink col-span-12">
                        This is an UNCODED MEDICATION- No Clinical Check will be
                        performed.
                      </p>
                    </>
                  )}
                </>
              )}
              <CommonSelect
                onChange={(value) => setSource(value)}
                isCreatable={false}
                options={sourceOptions}
                label="Source"
                isRequired={true}
                value={source}
                className="col-span-12"
                error={false}
              />
              <CustomInput
                name="diagnosisDate"
                type="date"
                className="col-span-12"
                label="Diagnosis Date"
                placeholder="Select Date"
                value={diagnosisDate}
                dateOptionValue={dateOption}
                showDateOptions={true}
                onDateOptionChange={handleDateOptionChange}
                showOptionalLabel={true}
              />

              <CustomInput
                name="comments"
                className="col-span-12"
                label="Comments"
                placeholder="Comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                type="textarea"
                inputClassName="min-h-[48px] max-h-[72px] overflow-y-auto resize-none whitespace-pre-wrap break-words leading-6"
              />
              <div className="col-span-12 text-sm font-normal pointer-events-none">
                <input type="checkbox" />
                <label className="ml-2 opacity-40">
                  Add Comments to Clinical Alerts - Coming Soon!
                </label>
              </div>
            </div>
            {/* <div className="absolute bottom-0">
              <div className="grid grid-col-12 gap-6">
              </div>
            </div> */}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center justify-center space-x-5">
            <Button
              className="min-w-[200px]"
              text="Cancel"
              type="secondary"
              onClick={() => {
                resetForm();
                onClose();
              }}
            />
            {create && (
              <Button
                className="min-w-[200px]"
                text="Save & Add Another"
                type="primary"
                disabled={isDuplicateCondition}
                onClick={() => {
                  if (conditionNameInputMode) {
                    setAddAnotherHistory(true);
                    setOpenUncodedDrug(true);
                  } else {
                    manageStep(true);
                  }
                }}
              />
            )}
            <Button
              className="min-w-[200px]"
              text={create ? "Save" : "Update & Save"}
              type="primary"
              disabled={isDuplicateCondition}
              onClick={() => {
                if (conditionNameInputMode) {
                  setOpenUncodedDrug(true);
                } else {
                  manageStep();
                }
              }}
            />
          </div>
        </div>
        <ConfirmationModalNew
          open={openUncodedDrug}
          title={"Uncoded Drug Alert"}
          type={"primary"}
          confirmText={"Continue"}
          cancelText="Cancel & Review"
          content={() => (
            <p>
              An uncoded Medical Condition has been added. Profile Analysis
              cannot be performed. A manual clinical check will be required.
            </p>
          )}
          onCross={() => {
            setAddAnotherHistory(false);
            setOpenUncodedDrug(false);
          }}
          handleClose={() => {
            setAddAnotherHistory(false);
            setOpenUncodedDrug(false);
          }}
          confirmAction={() => {
            if (addAnotherHistory) {
              setAddAnotherHistory(false);
              setConditionNameInputMode(false);
              setOpenUncodedDrug(false);
              manageStep();
              resetForm();
            } else {
              setConditionNameInputMode(false);
              setOpenUncodedDrug(false);
              manageStep();
              onClose();
            }
          }}
        />
      </div>
    </CommonSlider>
  );
}
